/**
 * Sampling i18n namespace — FR default + AR (RTL). Registered once in the module's onRegister().
 * Keys are flat + dot-namespaced under `sampling.*` / `nav.sampling*`. Missing AR -> FR fallback.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.sampling": "Prélèvement",
  "nav.sampling.queue": "File d'attente",
  "nav.sampling.benches": "Paillasses",
  "nav.sampling.print": "Matricule / vignette",

  // shared
  "sampling.priorite.urgent": "Urgent",
  "sampling.priorite.routine": "Routine",
  "sampling.statut.en_attente": "En attente",
  "sampling.statut.appele": "Appelé",
  "sampling.statut.absent": "Absent",
  "sampling.statut.preleve": "Prélevé",
  "sampling.statut.achemine": "Acheminé",
  "sampling.statut.termine": "Terminé",
  "sampling.statut.annule": "Annulé",
  "sampling.ticket.numero": "N°",
  "sampling.ticket.patient": "Patient",
  "sampling.ticket.anonyme": "—",
  "sampling.ticket.tubes": "Tubes",
  "sampling.col.actions": "Actions",

  // queue screen
  "sampling.queue.title": "File d'attente — prélèvement",
  "sampling.queue.room": "Salle",
  "sampling.queue.waiting": "{n} en attente",
  "sampling.queue.callNext": "Appeler le suivant",
  "sampling.queue.call": "Appeler",
  "sampling.queue.absent": "Absent",
  "sampling.queue.draw": "Prélever",
  "sampling.queue.route": "Acheminer",
  "sampling.queue.finish": "Terminer",
  "sampling.queue.empty": "Aucun patient en file",
  "sampling.queue.emptyDesc": "La salle est vide pour le moment.",
  "sampling.queue.called.success": "Ticket n° {n} appelé.",
  "sampling.queue.callNext.success": "Suivant appelé : n° {n}.",
  "sampling.queue.callNext.none": "Aucun ticket en attente à appeler.",
  "sampling.queue.absent.success": "Ticket n° {n} marqué absent et remis en file.",
  "sampling.queue.routed.success": "Ticket n° {n} acheminé au laboratoire.",
  "sampling.queue.finished.success": "Ticket n° {n} terminé.",

  // draw dialog
  "sampling.draw.title": "Prélèvement — ticket n° {n}",
  "sampling.draw.tube": "Tube",
  "sampling.draw.outcome": "Résultat",
  "sampling.draw.outcome.effectue": "Effectué",
  "sampling.draw.outcome.partiel": "Partiel",
  "sampling.draw.outcome.echoue": "Échoué",
  "sampling.draw.volume": "Volume (µL)",
  "sampling.draw.motif": "Motif d'échec",
  "sampling.draw.motif.placeholder": "Choisir un motif",
  "sampling.draw.motif.veine_introuvable": "Veine introuvable",
  "sampling.draw.motif.patient_agite": "Patient agité",
  "sampling.draw.motif.tube_casse": "Tube cassé",
  "sampling.draw.motif.volume_insuffisant": "Volume insuffisant",
  "sampling.draw.motif.hemolyse_visible": "Hémolyse visible",
  "sampling.draw.motif.refus_patient": "Refus du patient",
  "sampling.draw.notes": "Notes",
  "sampling.draw.noTubes": "Aucun tube rattaché à ce ticket.",
  "sampling.draw.submit": "Enregistrer le prélèvement",
  "sampling.draw.cancel": "Annuler",
  "sampling.draw.success": "Prélèvement enregistré pour le ticket n° {n}.",
  "sampling.draw.motifRequired": "Le motif est requis pour un échec.",
  "sampling.draw.redraw": "Reprise (nouveau tube)",
  "sampling.draw.redrawHint": "Saisir le matricule du tube réémis par la réception.",
  "sampling.draw.redrawMatricule": "Matricule du nouveau tube",
  "sampling.draw.redrawSubmit": "Enregistrer la reprise",
  "sampling.draw.redraw.success": "Reprise enregistrée.",
  "sampling.draw.redraw.resolveFail": "Tube introuvable pour ce matricule.",

  // benches screen
  "sampling.benches.title": "Paillasses — files par discipline",
  "sampling.benches.discipline": "Discipline",
  "sampling.benches.name": "File",
  "sampling.benches.active": "Active",
  "sampling.benches.inactive": "Inactive",
  "sampling.benches.empty": "Aucune paillasse configurée",
  "sampling.benches.emptyDesc": "Les files par discipline apparaîtront après le premier acheminement.",
  "sampling.benches.scan": "Scanner / acheminer un tube",

  // scan + enqueue
  "sampling.scan.title": "Acheminement par scan",
  "sampling.scan.input": "Matricule ou code scanné",
  "sampling.scan.inputPlaceholder": "Scanner le QR ou saisir le matricule",
  "sampling.scan.resolve": "Résoudre",
  "sampling.scan.resolved": "Tube résolu : {m}",
  "sampling.scan.notResolved": "Ce code n'est pas (ou plus) valide.",
  "sampling.scan.disciplines": "Disciplines",
  "sampling.scan.offline": "Mode dégradé (discipline principale seule)",
  "sampling.scan.enqueue": "Acheminer vers les paillasses",
  "sampling.scan.enqueued": "Tube acheminé vers {n} file(s).",
  "sampling.scan.reset": "Nouveau scan",

  // print / vignette
  "sampling.print.title": "Matricule / vignette",
  "sampling.print.subtitle": "Génération de l'étiquette à coller sur le tube.",
  "sampling.print.matricule": "Matricule",
  "sampling.print.token": "Jeton (QR)",
  "sampling.print.nom": "Nom",
  "sampling.print.prenom": "Prénom",
  "sampling.print.demande": "Réf. demande",
  "sampling.print.generate": "Générer la vignette",
  "sampling.print.print": "Imprimer",
  "sampling.print.previewTitle": "Aperçu de la vignette",
  "sampling.print.empty": "Renseignez au moins un champ puis générez la vignette.",
  "sampling.print.generated": "Vignette générée.",
  "sampling.print.qr": "QR : {t}",
  "sampling.print.noToken": "(pas de jeton QR)",

  // forbidden hints (action-level)
  "sampling.forbidden.action": "Vous n'avez pas le droit d'effectuer cette action.",
};

const AR: Record<string, string> = {
  "nav.section.sampling": "أخذ العينات",
  "nav.sampling.queue": "قائمة الانتظار",
  "nav.sampling.benches": "المناضد",
  "nav.sampling.print": "المعرّف / الملصق",

  "sampling.priorite.urgent": "عاجل",
  "sampling.priorite.routine": "عادي",
  "sampling.statut.en_attente": "في الانتظار",
  "sampling.statut.appele": "تم النداء",
  "sampling.statut.absent": "غائب",
  "sampling.statut.preleve": "تم السحب",
  "sampling.statut.achemine": "تم التوجيه",
  "sampling.statut.termine": "منتهٍ",
  "sampling.statut.annule": "ملغى",
  "sampling.ticket.numero": "رقم",
  "sampling.ticket.patient": "المريض",
  "sampling.ticket.anonyme": "—",
  "sampling.ticket.tubes": "الأنابيب",
  "sampling.col.actions": "إجراءات",

  "sampling.queue.title": "قائمة انتظار أخذ العينات",
  "sampling.queue.room": "القاعة",
  "sampling.queue.waiting": "{n} في الانتظار",
  "sampling.queue.callNext": "نداء التالي",
  "sampling.queue.call": "نداء",
  "sampling.queue.absent": "غائب",
  "sampling.queue.draw": "سحب",
  "sampling.queue.route": "توجيه",
  "sampling.queue.finish": "إنهاء",
  "sampling.queue.empty": "لا يوجد مرضى في القائمة",
  "sampling.queue.emptyDesc": "القاعة فارغة حالياً.",
  "sampling.queue.called.success": "تم نداء التذكرة رقم {n}.",
  "sampling.queue.callNext.success": "تم نداء التالي: رقم {n}.",
  "sampling.queue.callNext.none": "لا توجد تذكرة في الانتظار للنداء.",
  "sampling.queue.absent.success": "وُسمت التذكرة رقم {n} كغائب وأُعيدت للقائمة.",
  "sampling.queue.routed.success": "تم توجيه التذكرة رقم {n} إلى المختبر.",
  "sampling.queue.finished.success": "انتهت التذكرة رقم {n}.",

  "sampling.draw.title": "سحب — التذكرة رقم {n}",
  "sampling.draw.tube": "أنبوب",
  "sampling.draw.outcome": "النتيجة",
  "sampling.draw.outcome.effectue": "تم",
  "sampling.draw.outcome.partiel": "جزئي",
  "sampling.draw.outcome.echoue": "فشل",
  "sampling.draw.volume": "الحجم (ميكرولتر)",
  "sampling.draw.motif": "سبب الفشل",
  "sampling.draw.motif.placeholder": "اختر سبباً",
  "sampling.draw.motif.veine_introuvable": "تعذّر إيجاد الوريد",
  "sampling.draw.motif.patient_agite": "المريض غير مستقر",
  "sampling.draw.motif.tube_casse": "أنبوب مكسور",
  "sampling.draw.motif.volume_insuffisant": "حجم غير كافٍ",
  "sampling.draw.motif.hemolyse_visible": "انحلال دموي ظاهر",
  "sampling.draw.motif.refus_patient": "رفض المريض",
  "sampling.draw.notes": "ملاحظات",
  "sampling.draw.noTubes": "لا يوجد أنبوب مرتبط بهذه التذكرة.",
  "sampling.draw.submit": "حفظ السحب",
  "sampling.draw.cancel": "إلغاء",
  "sampling.draw.success": "تم حفظ السحب للتذكرة رقم {n}.",
  "sampling.draw.motifRequired": "السبب مطلوب عند الفشل.",
  "sampling.draw.redraw": "إعادة السحب (أنبوب جديد)",
  "sampling.draw.redrawHint": "أدخل معرّف الأنبوب المعاد إصداره من الاستقبال.",
  "sampling.draw.redrawMatricule": "معرّف الأنبوب الجديد",
  "sampling.draw.redrawSubmit": "حفظ إعادة السحب",
  "sampling.draw.redraw.success": "تم حفظ إعادة السحب.",
  "sampling.draw.redraw.resolveFail": "لم يُعثر على أنبوب بهذا المعرّف.",

  "sampling.benches.title": "المناضد — قوائم حسب التخصص",
  "sampling.benches.discipline": "التخصص",
  "sampling.benches.name": "القائمة",
  "sampling.benches.active": "نشطة",
  "sampling.benches.inactive": "غير نشطة",
  "sampling.benches.empty": "لا توجد منضدة مهيأة",
  "sampling.benches.emptyDesc": "ستظهر القوائم حسب التخصص بعد أول توجيه.",
  "sampling.benches.scan": "مسح / توجيه أنبوب",

  "sampling.scan.title": "التوجيه عبر المسح",
  "sampling.scan.input": "المعرّف أو الرمز الممسوح",
  "sampling.scan.inputPlaceholder": "امسح رمز QR أو أدخل المعرّف",
  "sampling.scan.resolve": "تحديد",
  "sampling.scan.resolved": "تم تحديد الأنبوب: {m}",
  "sampling.scan.notResolved": "هذا الرمز غير صالح (أو لم يعد كذلك).",
  "sampling.scan.disciplines": "التخصصات",
  "sampling.scan.offline": "وضع متدهور (التخصص الرئيسي فقط)",
  "sampling.scan.enqueue": "توجيه إلى المناضد",
  "sampling.scan.enqueued": "تم توجيه الأنبوب إلى {n} قائمة.",
  "sampling.scan.reset": "مسح جديد",

  "sampling.print.title": "المعرّف / الملصق",
  "sampling.print.subtitle": "إنشاء الملصق للصقه على الأنبوب.",
  "sampling.print.matricule": "المعرّف",
  "sampling.print.token": "الرمز (QR)",
  "sampling.print.nom": "اللقب",
  "sampling.print.prenom": "الاسم",
  "sampling.print.demande": "مرجع الطلب",
  "sampling.print.generate": "إنشاء الملصق",
  "sampling.print.print": "طباعة",
  "sampling.print.previewTitle": "معاينة الملصق",
  "sampling.print.empty": "أدخل حقلاً واحداً على الأقل ثم أنشئ الملصق.",
  "sampling.print.generated": "تم إنشاء الملصق.",
  "sampling.print.qr": "QR: {t}",
  "sampling.print.noToken": "(لا يوجد رمز QR)",

  "sampling.forbidden.action": "لا تملك صلاحية تنفيذ هذا الإجراء.",
};

let registered = false;

/** Register the sampling namespace exactly once (idempotent — safe under HMR/double-register). */
export function registerSamplingMessages(): void {
  if (registered) return;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
  registered = true;
}
